import React, { useState, useEffect, useCallback } from 'react';
import './DirectConversations.css';
import DirectChat from './DirectChat';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const DirectConversations = ({ token, userData }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [activeConv, setActiveConv]       = useState(null);

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/direct/conversations`, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      const data = await res.json();
      if (res.ok && data.success && Array.isArray(data.data)) {
        setConversations(data.data);
      } else {
        setConversations([]);
      }
    } catch (err) {
      console.error('[DirectConversations] Fetch error:', err);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const formatTime = (ts) => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      const now = new Date();
      const diffDays = Math.floor((now - d) / 86400000);
      if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7)  return d.toLocaleDateString([], { weekday: 'short' });
      return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
    } catch { return ''; }
  };

  // ── Open a conversation ───────────────────────────────────────────────────
  if (activeConv) {
    return (
      <DirectChat
        conversation={activeConv}
        userData={userData}
        token={token}
        onBack={() => {
          setActiveConv(null);
          fetchConversations(); // refresh unread counts
        }}
      />
    );
  }

  return (
    <div className="dconv-container">
      {/* Header */}
      <div className="dconv-header">
        <h2>Direct Messages</h2>
        <button className="dconv-refresh-btn" onClick={fetchConversations} title="Refresh">
          🔄
        </button>
      </div>

      {/* Body */}
      <div className="dconv-body">
        {loading ? (
          <div className="dconv-state">
            <p>Loading conversations…</p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="dconv-state">
            <span>💬</span>
            <p>No direct conversations yet</p>
            <p className="dconv-state-sub">
              A chat will appear here once a lawyer accepts your request
            </p>
          </div>
        ) : (
          <div className="dconv-list">
            {conversations.map(conv => {
              const initials = conv.other_user?.name?.charAt(0)?.toUpperCase() || '?';
              const lastMsg = conv.last_message?.message || (conv.last_message ? '📎 File' : 'No messages yet');
              const lastTime = conv.last_message?.created_at ? formatTime(conv.last_message.created_at) : '';
              const unread = conv.unread_count || 0;

              return (
                <div
                  key={conv.guest_id}
                  className={`dconv-item ${unread > 0 ? 'dconv-item--unread' : ''}`}
                  onClick={() => setActiveConv(conv)}
                >
                  {/* Avatar */}
                  <div className="dconv-avatar">
                    <div className="dconv-avatar-placeholder">{initials}</div>
                  </div>

                  {/* Info */}
                  <div className="dconv-info">
                    <div className="dconv-info-top">
                      <p className="dconv-name">
                        {conv.title || conv.other_user?.name || 'Direct Chat'}
                      </p>
                      <span className="dconv-time">{lastTime}</span>
                    </div>
                    <div className="dconv-info-bottom">
                      <p className="dconv-last-msg">{lastMsg}</p>
                      {unread > 0 && (
                        <span className="dconv-unread-badge">{unread}</span>
                      )}
                    </div>
                    {conv.case_type && (
                      <span className="dconv-case-type">{conv.case_type}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectConversations;
